/**
 * i18n bootstrap, shared by both applications.
 *
 * One instance, two namespaces: `common` from this package (chrome, actions, errors) and `app`
 * from whichever application is running. That split keeps a button label from being translated
 * twice in two different ways.
 *
 * Golden rule 7: Arabic and English are peers. Neither is a fallback for the other in the
 * sense of being "the real one" — but a missing Arabic key must still render something, so
 * English is the technical fallback and a missing key is a bug to fix, not a state to live in.
 */

import i18next, { type i18n as I18nInstance, type Resource } from "i18next";
import { initReactI18next } from "react-i18next";

import authAr from "../locales/auth.ar.json";
import authEn from "../locales/auth.en.json";
import commonAr from "../locales/ar.json";
import commonEn from "../locales/en.json";

export const SUPPORTED_LANGUAGES = ["en", "ar"] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

/** Right-to-left languages. Arabic is the only one in M1; the list is the extension point. */
const RTL_LANGUAGES = new Set<Language>(["ar"]);

export const DEFAULT_NAMESPACE = "common";
export const APP_NAMESPACE = "app";

/**
 * Sign-in, the forced password change, and the session menu.
 *
 * Its own namespace rather than a section of `common` because the strings belong to the shared
 * package but are shown *before* an application exists — the login page renders with no app
 * namespace loaded for the screen behind it.
 */
export const AUTH_NAMESPACE = "auth";

export type Direction = "ltr" | "rtl";

export function directionOf(language: string): Direction {
  return RTL_LANGUAGES.has(language as Language) ? "rtl" : "ltr";
}

export function isSupported(language: string): language is Language {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(language);
}

/** Key the chosen language is persisted under, so a reload does not reset an Arabic user. */
export const LANGUAGE_STORAGE_KEY = "nab.language";

function initialLanguage(fallback: Language): Language {
  if (typeof window === "undefined") return fallback;

  const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (stored && isSupported(stored)) return stored;

  // `ar-LY` and `ar` both mean Arabic here.
  const browser = window.navigator.language?.split("-")[0];
  if (browser && isSupported(browser)) return browser;

  return fallback;
}

export interface InitI18nOptions {
  /** Application-specific resources, keyed by language, for the `app` namespace. */
  appResources: Record<Language, Resource[string]>;
  /** Used when neither storage nor the browser expresses a supported preference. */
  defaultLanguage?: Language;
}

export function initI18n({
  appResources,
  defaultLanguage = "en",
}: InitI18nOptions): I18nInstance {
  const language = initialLanguage(defaultLanguage);

  void i18next.use(initReactI18next).init({
    lng: language,
    fallbackLng: "en",
    supportedLngs: [...SUPPORTED_LANGUAGES],
    defaultNS: DEFAULT_NAMESPACE,
    ns: [DEFAULT_NAMESPACE, APP_NAMESPACE, AUTH_NAMESPACE],
    resources: {
      en: {
        [DEFAULT_NAMESPACE]: commonEn,
        [APP_NAMESPACE]: appResources.en,
        [AUTH_NAMESPACE]: authEn,
      },
      ar: {
        [DEFAULT_NAMESPACE]: commonAr,
        [APP_NAMESPACE]: appResources.ar,
        [AUTH_NAMESPACE]: authAr,
      },
    },
    interpolation: {
      // React escapes for us; double-escaping mangles Arabic punctuation.
      escapeValue: false,
    },
    // A missing key must be loud in development and harmless in production.
    saveMissing: false,
    returnEmptyString: false,
  });

  applyDocumentLanguage(language);
  return i18next;
}

/**
 * Put the language and direction on the document element.
 *
 * `dir` on `<html>` rather than on a wrapper `<div>`: it has to cover scrollbars, native form
 * controls, and anything rendered in a portal outside the React tree.
 */
export function applyDocumentLanguage(language: string): void {
  if (typeof document === "undefined") return;
  document.documentElement.lang = language;
  document.documentElement.dir = directionOf(language);
}
